import React, { useEffect, useState } from "react";

import api from "../services/api";
import TransactionStatusBadge from "./TransactionStatusBadge";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(Number(value || 0));

const signForType = (type = "") => {
  const normalized = String(type).toLowerCase();
  if (normalized.includes("withdraw") || normalized.includes("transfer out")) {
    return -1;
  }
  return 1;
};

const TransactionHistoryModal = ({ onClose }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await api.getHistory();
        if (!mounted) return;
        const list = Array.isArray(data) ? data : [];
        setTransactions(list);
      } catch (error) {
        console.error("Failed to fetch transactions", error);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 px-4 py-6">
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Transaction history</h2>
          <button
            onClick={onClose}
            className="rounded-full bg-slate-100 p-2 text-slate-600 hover:bg-slate-200"
            aria-label="Close"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="px-6 py-12 text-center text-sm text-slate-500">Loading transactions...</div>
        ) : transactions.length === 0 ? (
          <p className="px-6 py-8 text-sm text-slate-500">No transactions found yet.</p>
        ) : (
          <div className="max-h-[70vh] overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-white text-left text-slate-500">
                <tr>
                  <th className="px-6 py-3 font-medium">Type</th>
                  <th className="px-6 py-3 font-medium">Amount</th>
                  <th className="px-6 py-3 font-medium">Note</th>
                  <th className="px-6 py-3 font-medium">Timestamp</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {transactions.map((txn) => {
                  const type = txn.type || txn.transaction_type || "-";
                  const signedAmount = Number(txn.amount || 0) * signForType(type);
                  const affectsBalance = txn.affects_balance !== false;
                  return (
                    <tr key={txn.id || `${type}-${txn.timestamp}`} className="hover:bg-slate-50">
                      <td className="px-6 py-3 capitalize text-slate-700">{type}</td>
                      <td className="px-6 py-3 text-slate-900">{formatCurrency(signedAmount)}</td>
                      <td className="px-6 py-3 text-slate-600">{txn.note || ""}</td>
                      <td className="px-6 py-3 text-slate-600">
                        {txn.timestamp ? new Date(txn.timestamp).toLocaleString() : ""}
                      </td>
                      <td className="px-6 py-3">
                        <TransactionStatusBadge status={txn.status || "successful"} label={txn.status_label} />
                        {!affectsBalance && (
                          <p className="mt-1 text-xs text-slate-500">
                            {txn.status_help || "Not posted yet"}
                          </p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistoryModal;
